import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";

import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import type { CalendarEventData } from "../../common/components/EventNote";
import { MatrixHero } from "../../common/components/MatrixHero";
import "../../scss/calendar.scss";
import { useI18n } from "../../i18n/I18nProvider";
import { fetchUpcomingEvents, type ApiEvent } from "../../api/eventsapi";

/* ═══════════════ HELPERS ═══════════════ */

const isOccurrence = (id: string | undefined) =>{
    return id != null && id.includes("occurrence");
}

type TemporalState = "past" | "in-progress" | "future";


const getTemporalState = (start: string, end: string): TemporalState => {
    const now = Date.now();
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (now >= s && now <= e) return "in-progress";
    if (now > e) return "past";
    return "future";
};


const stateColor = (state: TemporalState) => {
    switch (state) {
        case "in-progress": return "#f0ad4e";   // orange
        case "past":        return "#d1d5db";   // light gray / white-ish
        case "future":      return "#69f13D";   // green
    }
};


const stateBorder = (state: TemporalState) => {
    switch (state) {
        case "past": return "#d1d5db";
        default:     return stateColor(state);
    }
};


const mapApiEvent = (e: ApiEvent): CalendarEventData => ({
    id: e.id,
    title: e.name,
    startDate: e.scheduled_start_time,
    endDate: e.scheduled_end_time,
    location: e.location,
    description: e.description,
    eventMode: e.event_mode,
    userCount: e.user_count,
    isNew: e.is_new,
});


const formatDateRange = (start: string, end: string, locale: string) => {
    const loc = locale === "en" ? "en-GB" : "pl-PL";
    const opts: Intl.DateTimeFormatOptions = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    };
    const s = new Date(start).toLocaleDateString(loc, opts);
    const e = new Date(end).toLocaleDateString(loc, opts);
    return s === e ? s : `${s}  —  ${e}`;
};


const currentMonthStart = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

/* ═══════════════ COMPONENT ═══════════════ */

const CalendarComponent: React.FC = () => {
    const [events, setEvents] = useState<CalendarEventData[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEventData | null>(null);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);



    const introRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<HTMLDivElement>(null);
    const panelCloseRef = useRef<HTMLButtonElement>(null);

    const { t, language } = useI18n();

   
    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchUpcomingEvents()
            .then((apiEvents) => {
                if (!cancelled) {
                    setEvents(apiEvents.map(mapApiEvent));
                }
            })
            .catch((err) => {
                if (!cancelled) setError(err.message);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => { cancelled = true; };
    }, []);

   
    useEffect(() => {
        const obs = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) e.target.classList.add("--visible");
                }),
            { threshold: 0.15 }
        );
        [introRef, gridRef].forEach((r) => r.current && obs.observe(r.current));
        return () => obs.disconnect();
    }, []);

   
    useEffect(() => {
        if (isPanelOpen) {
            const timer = setTimeout(() => panelCloseRef.current?.focus(), 60);
            return () => clearTimeout(timer);
        }
    }, [isPanelOpen]);

    const handlePanelKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Escape") {
                setIsPanelOpen(false);
            }
        },
        []
    );



   
    const calendarEvents = useMemo(() => events.map((event) => {
        const state = getTemporalState(event.startDate, event.endDate);
        return {
            id: event.id,
            title: event.title,
            start: event.startDate,
            // NO `end` — event renders on a single day only
            allDay: false,
            backgroundColor: "transparent",
            borderColor: "transparent",
            extendedProps: { ...event, _state: state },
        };
    }), [events]);

    const handleEventClick = (info: any) => {
        setSelectedEvent(info.event.extendedProps as CalendarEventData);
        setIsPanelOpen(true);
    };

    const renderEventContent = (arg: any) => {
        const ev = arg.event.extendedProps as CalendarEventData & { _state: TemporalState };
        const state = ev._state ?? getTemporalState(ev.startDate, ev.endDate);
        const color = stateColor(state);
        const border = stateBorder(state);
        const isPast = state === "past";

        return (
            <button
                className={`cal-event-btn ${isPast ? "cal-event-btn--past" : ""} ${state === "in-progress" ? "cal-event-btn--active" : ""}`}
                type="button"
                aria-label={ev.title}
                style={{
                    borderColor: border,
                    ...(state === "in-progress" ? { background: "rgba(240, 173, 78, 0.12)" } : {}),
                    ...(isPast ? { background: "rgba(107, 114, 128, 0.08)" } : {}),
                }}
            >
                <span
                    className="cal-event-dot"
                    style={{ background: color }}
                    aria-hidden="true"
                />
                <span className={`cal-event-label ${isPast ? "cal-event-label--past" : ""}`}>
                    {arg.event.title}
                </span>
                {state === "in-progress" && !isOccurrence(ev.id) && (
                    <span className="cal-event-live" aria-label={t("calendar_badge_live")}>LIVE</span>
                )}
                {ev.isNew && state !== "past" && (
                    <span className="cal-event-new" aria-label={t("calendar_badge_new")}>NEW</span>
                )}
            </button>
        );
    };

   
    const modeLabel = (mode: string) => {
        switch (mode) {
            case "online":  return t("calendar_mode_online");
            case "offline": return t("calendar_mode_offline");
            case "hybrid":  return t("calendar_mode_hybrid");
            default:        return mode;
        }
    };

    
    const modeColor = (mode: string) => {
        switch (mode) {
            case "online":  return "#69f13D";
            case "offline": return "#38bdf8";
            case "hybrid":  return "#f0ad4e";
            default:        return "#69f13D";
        }
    };

    return (
        <div className="cal-page">
            {/* ── HERO ── */}
            <MatrixHero title={t("calendar_hero_title")} subtitle={t("calendar_hero_subtitle")} />

            <div className="cal-page__bg">
                {/* ── INTRO ── */}
                <section className="cal-page__section">
                    <div ref={introRef} className="cal-page__intro">
                        <h1 className="cal-page__title">{t("calendar_page_title")}</h1>
                        <p className="cal-page__subtitle">{t("calendar_page_subtitle")}</p>
                    </div>
                </section>

                {/* ── CALENDAR ── */}
                <section className="cal-page__section">
                    <div ref={gridRef} className="cal-page__grid-wrap">
                        {isLoading && (
                            <p className="cal-page__loading" role="status" aria-live="polite">
                                {t("calendar_loading")}
                            </p>
                        )}
                        {error && (
                            <p className="cal-page__error" role="alert">
                                {t("calendar_error")}: {error}
                            </p>
                        )}
                        <div className="cal-view">
                            <FullCalendar
                                plugins={[dayGridPlugin, interactionPlugin]}
                                initialView="dayGridMonth"
                                firstDay={1}
                                locale={language === "en" ? "en" : "pl"}
                                events={calendarEvents}
                                eventClick={handleEventClick}
                                eventContent={renderEventContent}
                                headerToolbar={{
                                    left: "prev,next",
                                    center: "title",
                                    right: "today",
                                }}
                                buttonText={{ today: language === "en" ? "Today" : "Dziś" }}
                                validRange={{ start: currentMonthStart() }}
                                height="auto"
                                dayMaxEvents={3}
                                eventDisplay="block"
                                contentHeight={900}
                            />
                        </div>
                    </div>
                </section>
            </div>

            {/* ── BLUR OVERLAY ── */}
            <div
                className={`cal-overlay ${isPanelOpen ? "is-visible" : ""}`}
                onClick={() => setIsPanelOpen(false)}
                aria-hidden="true"
            />

            {/* ── SIDE PANEL ── */}
            <aside
                className={`cal-panel ${isPanelOpen ? "is-open" : ""} ${isPanelOpen && selectedEvent && getTemporalState(selectedEvent.startDate, selectedEvent.endDate) === "past" ? "cal-panel--past" : ""}`}
                role="dialog"
                aria-modal="true"
                aria-label={selectedEvent?.title ?? t("calendar_label_details")}
                onKeyDown={handlePanelKeyDown}
            >
                {selectedEvent && (
                    <div className="cal-panel__inner">
                        <button
                            ref={panelCloseRef}
                            className="cal-panel__close"
                            onClick={() => setIsPanelOpen(false)}
                            aria-label={t("calendar_close")}
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                        </button>

                        <header className="cal-panel__header">
                            {/* ── badges row ── */}
                            <div className="cal-panel__tags">
                                {/* Mode badge */}
                                <span
                                    className="cal-panel__tag cal-panel__tag--mode"
                                    style={{
                                        borderColor: `${modeColor(selectedEvent.eventMode)}40`,
                                        background: `${modeColor(selectedEvent.eventMode)}1a`,
                                        color: modeColor(selectedEvent.eventMode),
                                    }}
                                >
                                    {selectedEvent.eventMode === "online" ? (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                    ) : selectedEvent.eventMode === "offline" ? (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                                            <circle cx="12" cy="10" r="3" />
                                        </svg>
                                    ) : (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                                        </svg>
                                    )}
                                    {modeLabel(selectedEvent.eventMode)}
                                </span>

                                {/* Temporal state badge */}
                                {(() => {
                                    const state = getTemporalState(selectedEvent.startDate, selectedEvent.endDate);
                                    if (state === "in-progress" && !isOccurrence(selectedEvent.id)) return (
                                        <span className="cal-panel__tag cal-panel__tag--live">
                                            <span className="cal-panel__live-dot" aria-hidden="true" />
                                            LIVE
                                        </span>
                                    );
                                    if (state === "past") return (
                                        <span className="cal-panel__tag cal-panel__tag--past">
                                            {t("calendar_status_past")}
                                        </span>
                                    );
                                    return null;
                                })()}

                                {/* NEW badge */}
                                {selectedEvent.isNew && (
                                    <span className="cal-panel__tag cal-panel__tag--new">
                                        NEW
                                    </span>
                                )}
                            </div>

                            <h2 className="cal-panel__title">{selectedEvent.title}</h2>

                            <time className="cal-panel__date">
                                {formatDateRange(selectedEvent.startDate, selectedEvent.endDate, language)}
                            </time>
                        </header>

                        <section className="cal-panel__body">
                            {/* Location */}
                            <div className="cal-panel__group">
                                <div className="cal-panel__label">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                    {t("calendar_label_location")}
                                </div>
                                <p className="cal-panel__value">{selectedEvent.location}</p>
                            </div>

                            {/* Participants */}
                            <div className="cal-panel__group">
                                <div className="cal-panel__label">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                                    </svg>
                                    {t("calendar_label_participants")}
                                </div>
                                <p className="cal-panel__value">
                                    {selectedEvent.userCount} {t("calendar_label_people")}
                                </p>
                            </div>

                            {/* Description */}
                            <div className="cal-panel__group">
                                <div className="cal-panel__label">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="16" y1="13" x2="8" y2="13" />
                                        <line x1="16" y1="17" x2="8" y2="17" />
                                    </svg>
                                    {t("calendar_label_details")}
                                </div>
                                <p className="cal-panel__desc">{selectedEvent.description}</p>
                            </div>
                        </section>
                    </div>
                )}
            </aside>
        </div>
    );
};

export default CalendarComponent;
